import { useEffect, useRef, useState, useCallback } from 'react';
import { apiUrl, refreshSession } from '../apiBase';

// Idle-based session management with silent access refresh.
export function useSessionTimers({
  syncIntervalSec = 60,
  idleWarnLead = 180,
  silentRefreshThreshold = 60,   // refresh only in last 60s of access token by default
  recentActivityWindow = 120,
  criticalSeconds = 30,
} = {}) {
  const [state, setState] = useState({
    accessRem: null,
    idleRem: null,
    absRem: null,
    serverTs: null,
    lastSync: Date.now(),
    authenticated: null,
  });
  const accessInitialRef = useRef(null);
  const lastRefreshClientTs = useRef(Date.now());
  const lastActivityTs = useRef(Date.now());
  const lastSilentRefreshTs = useRef(0);
  const idleRemRef = useRef(null);

  const accessInitialized = useRef(false);
  const applyPayload = useCallback((p) => {
    if (!p) return;
    const now = Date.now();
    let resetAccess = false;
    if (typeof p.access_expires_in === 'number') {
      if (!accessInitialized.current || p._forceAccessReset) {
        accessInitialRef.current = p.access_expires_in;
        lastRefreshClientTs.current = now;
        accessInitialized.current = true;
        resetAccess = true;
      }
    }
    setState(prev => ({
      ...prev,
      authenticated: p.authenticated !== undefined ? p.authenticated : (prev.authenticated ?? true),
      accessRem: resetAccess ? p.access_expires_in : prev.accessRem,
      idleRem: typeof p.refresh_idle_in === 'number' ? p.refresh_idle_in : prev.idleRem,
      absRem: typeof p.refresh_abs_in === 'number' ? p.refresh_abs_in : prev.absRem,
      serverTs: typeof p.server_ts === 'number' ? p.server_ts : prev.serverTs,
      lastSync: now,
    }));
  }, []);

  // Mirror idleRem into ref for silent refresh logic without re-binding effect
  useEffect(() => { idleRemRef.current = state.idleRem; }, [state.idleRem]);

  // Initial sync & periodic sync
  useEffect(() => {
    let cancelled = false;
    const fetchSession = async () => {
      try {
        let r = await fetch(apiUrl('/auth/session'), { credentials: 'include', cache: 'no-store' });
        if (!r.ok) return;
        let j = await r.json();
        if (cancelled) return;
        if (!j.authenticated && j.can_refresh === true) {
          const refreshed = await refreshSession();
          if (cancelled || !refreshed) return;
          r = await fetch(apiUrl('/auth/session'), { credentials: 'include', cache: 'no-store' });
          if (!r.ok) return;
          j = await r.json();
        }
        if (!cancelled) applyPayload({
          authenticated: !!j.authenticated,
          access_expires_in: j.access_expires_in,
          refresh_idle_in: j.refresh_idle_in,
          refresh_abs_in: j.refresh_abs_in,
          server_ts: j.server_ts,
          _forceAccessReset: false, // do not reset access countdown after first initialization
        });
      } catch (_) {}
    };
    fetchSession();
    const id = setInterval(fetchSession, syncIntervalSec * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [syncIntervalSec, applyPayload]);

  // Listen to unified refresh/login events
  useEffect(() => {
    const onUpdate = (e) => applyPayload(e.detail);
    window.addEventListener('kk:auth:sessionUpdated', onUpdate);
    return () => window.removeEventListener('kk:auth:sessionUpdated', onUpdate);
  }, [applyPayload]);

  // Activity listeners
  useEffect(() => {
    const mark = () => { lastActivityTs.current = Date.now(); };
    const events = ['mousemove','keydown','click','scroll','visibilitychange'];
    events.forEach(ev => window.addEventListener(ev, mark, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, mark));
  }, []);

  // Local ticking + silent refresh
  useEffect(() => {
    const tick = () => {
      setState(prev => {
        if (!prev.authenticated) return prev;
        const now = Date.now();
        const secondsSinceRefresh = Math.floor((now - lastRefreshClientTs.current) / 1000);
        const accessRem = accessInitialRef.current != null ? Math.max(0, accessInitialRef.current - secondsSinceRefresh) : null;
        return {
          ...prev,
          accessRem,
          idleRem: prev.idleRem != null ? Math.max(0, prev.idleRem - 1) : null,
          absRem: prev.absRem != null ? Math.max(0, prev.absRem - 1) : null,
        };
      });
      try {
        const nowMs = Date.now();
        const secondsSinceRefresh = Math.floor((nowMs - lastRefreshClientTs.current)/1000);
        const accessRem = accessInitialRef.current != null ? Math.max(0, accessInitialRef.current - secondsSinceRefresh) : null;
        if (accessRem != null && accessRem > 0 && accessRem <= silentRefreshThreshold) {
          const sinceActivity = (nowMs - lastActivityTs.current)/1000;
          // Require at least one activity after mount (activity window shorter than idleRem ensures genuine interaction)
          const sinceSilent = (nowMs - lastSilentRefreshTs.current)/1000;
          if (!tick._inFlight && sinceActivity < recentActivityWindow && sinceActivity < (idleRemRef.current || Number.MAX_SAFE_INTEGER) && sinceSilent > 15) {
            tick._inFlight = true;
            lastSilentRefreshTs.current = nowMs;
            fetch(apiUrl('/auth/refresh'), { method: 'POST', credentials: 'include' })
              .then(r => r.ok ? r.json().catch(()=>null) : null)
              .then(payload => {
                if (payload) {
                  window.dispatchEvent(new CustomEvent('kk:auth:sessionUpdated', { detail: {
                    access_expires_in: payload.access_expires_in,
                    refresh_idle_in: payload.refresh_idle_in,
                    refresh_abs_in: payload.refresh_abs_in,
                    server_ts: payload.server_ts,
                    _forceAccessReset: true,
                  }}));
                }
              })
              .finally(()=> { tick._inFlight = false; })
              .catch(()=>{});
          }
        }
      } catch {}
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [silentRefreshThreshold, recentActivityWindow]);

  const idleShowWarning = state.idleRem != null && state.idleRem <= idleWarnLead;
  const timeToExpiry = (state.idleRem != null && state.absRem != null)
    ? Math.min(state.idleRem, state.absRem)
    : (state.idleRem ?? state.absRem);
  const critical = timeToExpiry != null && timeToExpiry <= criticalSeconds;
  return { ...state, showWarning: idleShowWarning, critical, accessInitial: accessInitialRef.current };
}
